import Model from '@/store/plugins/orm/Model';
import { KitchenTicketItem, AppEvent, Product, Terminal } from './ModelRepository';
import HashGen from '@/utilities/HashGen';
import ApiService from '@/services/ApiService';

export class KitchenTicket extends Model {
    static get entity() {
        return 'kitchen_tickets';
    }

    static get primaryKey() {
        return 'hash';
    }

    static get TYPE_PICKUP() {
        return 'pickup';
    }
    static get TYPE_DELIVERY() {
        return 'delivery';
    }
    static get TYPE_DINE_IN() {
        return 'dineIn';
    }
    static get STATUS_PENDING() {
        return 'pending';
    }
    static get STATUS_DONE() {
        return 'done';
    }
    static get STATUS_CANCELED() {
        return 'canceled';
    }

    static get DATE_FORMAT() {
        return 'YYYY-MM-DDTHH:mm:ssZ';
    }

    static fields() {
        return {
            id: this.attr(null),
            hash: this.attr(HashGen.regular()),
            order_id: this.attr(null),
            order_hash: this.attr(null),
            user_id: this.attr(null),
            table_id: this.attr(null),
            type: this.attr(this.TYPE_DINE_IN),
            status: this.attr(this.STATUS_PENDING),
            issued_at: this.attr(null),
            last_activity_at: this.attr(null),
            ack_at: this.attr(null),
            updated_at: this.attr(null),
            created_at: this.attr(null),

            items: this.attr([], (value) => value.map((item) => new KitchenTicketItem(item))),

            sync_needed: this.attr(null),
        };
    }

    static async fetchLatest(initial = false) {
        if (!this.store().getters.isOnline) {
            return;
        }

        let params = {};
        if (!initial) {
            const lastUpdatedAt = KitchenTicket.getLastUpdatedAt();
            params = { last_updated_at: lastUpdatedAt ? lastUpdatedAt.utc().format('YYYY-MM-DD HH:mm:ss') : null };
        }

        const response = await ApiService.get('/kitchen-tickets/latest', { params: params });

        if (initial) {
            let kitchenTickets = KitchenTicket.all().filter((kt) => !!kt.sync_needed);
            KitchenTicket.create({ data: kitchenTickets.map((item) => item.$toJson()) });
        }

        KitchenTicket.insert({ data: response.data.records });
    }

    static getLastUpdatedAt() {
        return KitchenTicket.all().length
            ? moment(Math.max(...KitchenTicket.all().filter(kt => kt.updated_at).map(kt => moment(kt.updated_at).valueOf())))
            : null;
    }

    static createNew(order) {
        const kitchenOrderItems = order.items.filter((orderItem) => {
            const product = orderItem.getProduct();
            return product.type === 'food' || product.printing_sector === Product.PRINTING_SECTOR_KITCHEN;
        });

        if (!kitchenOrderItems.length) return null;

        const kitchenTicket = new KitchenTicket({
            order_id: order.id,
            order_hash: order.hash,
            user_id: order.user_id,
            table_id: order.table_id,
            type: KitchenTicket.TYPE_DINE_IN,
            status: KitchenTicket.STATUS_PENDING,
            issued_at: moment().utc().format(KitchenTicket.DATE_FORMAT),
            last_activity_at: moment().utc().format(KitchenTicket.DATE_FORMAT),
            ack_at: null,
        });

        kitchenTicket.items = kitchenOrderItems.map((orderItem) => {
            return new KitchenTicketItem({
                kitchen_ticket_id: kitchenTicket.id,
                kitchen_ticket_hash: kitchenTicket.hash,
                order_item_id: orderItem.id,
                order_item_hash: orderItem.hash,
                product_id: orderItem.product_id,
                name: orderItem.name,
                count: orderItem.count,
                comments: orderItem.comments,
            });
        });

        return kitchenTicket;
    }

    static async issue(order) {
        const kdsRegistered = Terminal.all().some(t => t.isKitchenDisplaySystem())
        if (!this.store().getters.modules.kds || !kdsRegistered) return { success: false }

        const kitchenTicket = KitchenTicket.createNew(order);
        if (!kitchenTicket) return { success: false };

        kitchenTicket.sync_needed = KitchenTicket.store().getters.tenant.venue.hash;
        return kitchenTicket.sync();
    }

    static async cancel(order) {
        const kdsRegistered = Terminal.all().some(t => t.isKitchenDisplaySystem())
        if (!this.store().getters.modules.kds || !kdsRegistered) return { success: false }

        const kitchenTicket = KitchenTicket.all().find(kt => kt.order_hash === order.hash)
        if (!kitchenTicket) return { success: false }

        kitchenTicket.status = KitchenTicket.STATUS_CANCELED;

        kitchenTicket.sync_needed = KitchenTicket.store().getters.tenant.venue.hash;
        return kitchenTicket.sync();
    }

    static async deleteItem(order, orderItem) {
        const kdsRegistered = Terminal.all().some(t => t.isKitchenDisplaySystem())
        if (!this.store().getters.modules.kds || !kdsRegistered) return { success: false }

        const kitchenTicket = KitchenTicket.all().find(kt => kt.order_hash === order.hash)
        if (!kitchenTicket) return { success: false }

        kitchenTicket.items = kitchenTicket.items.filter(i => i.order_item_hash !== orderItem.hash);

        kitchenTicket.sync_needed = KitchenTicket.store().getters.tenant.venue.hash;
        return kitchenTicket.sync(!kitchenTicket.items.length);
    }

    async complete() {
        this.last_activity_at = moment().utc().format(KitchenTicket.DATE_FORMAT);
        this.status = KitchenTicket.STATUS_DONE;

        this.sync_needed = this.$store().getters.tenant.venue.hash;
        return this.sync();
    }

    async recall() {
        this.last_activity_at = moment().utc().format(KitchenTicket.DATE_FORMAT);
        this.status = KitchenTicket.STATUS_PENDING;
        this.ack_at = null;

        this.sync_needed = this.$store().getters.tenant.venue.hash;
        return this.sync();
    }

    async acknowledge() {
        this.ack_at = moment().utc().format(KitchenTicket.DATE_FORMAT);

        this.sync_needed = this.$store().getters.tenant.venue.hash;
        return this.sync();
    }

    async completeItem(item, complete = true, sync = true) {
        const kitchenItem = this.items.find(i => i.hash === item.hash);
        kitchenItem.complete(complete);

        this.last_activity_at = moment().utc().format(KitchenTicket.DATE_FORMAT);

        if (sync) {
            this.sync_needed = this.$store().getters.tenant.venue.hash;
            return this.sync();
        }

        return { success: true };
    }

    async sync(remove = false) {
        const action = remove ? 'delete' : (!this.id ? 'post' : 'patch')
        const url = '/kitchen-tickets' + (this.id ? `/${this.id}` : '')
        let record = this.$toJson()

        if (action === 'delete') this.$delete()
        else this.$save()

        if (!this.$store().getters.isOnline) {
            AppEvent.build('kitchenTicketsSync', { items: [{ action, record }] }).send();
            return { success: true };
        }

        try {
            const response = await ApiService[action](url, record)

            if (response.data.record) {
                record = response.data.record
                this.$fill(record);
                KitchenTicket.insert({ data: [record] });
            }

            AppEvent.build('kitchenTicketsSync', { items: [{ action, record }] }).send();

            return { success: true };
        } catch (error) {
            return { success: false };
        }
    }

    static async onKitchenTicketsSync(event) {
        const deletedRecords = event.data.items.filter(i => i.action === 'delete').map(i => i.record)
        const updatedRecords = event.data.items.filter(i => i.action !== 'delete').map(i => i.record)

        if(updatedRecords.length) {
            KitchenTicket.insert({ data: updatedRecords })
        }

        deletedRecords.forEach(r => {
            const record = KitchenTicket.find(r.hash)
            record.$delete()
        })
    }

    isPending() {
        return this.status === KitchenTicket.STATUS_PENDING
    }

    isDone() {
        return this.status === KitchenTicket.STATUS_DONE
    }

    isCanceled() {
        return this.status === KitchenTicket.STATUS_CANCELED
    }

    isAcknowledged() {
        return !!this.ack_at
    }
}

export default KitchenTicket;
